/**
 * Soft barge-in: the user spoke OVER the agent — keep talking (a listener cue, agreement, a laugh) or cut and give them
 * the floor (a question, correction, "wait", new information)? Rules decide the clear cases at 0ms; a fast yes/no model
 * decides the rest within a budget. Undecided (no model, failed, late) → 'cut': today's hard barge is the safe default.
 */
import { forComponent } from '../logging';
import type { ChatLike, ChatResponse } from '../llm';

const log = forComponent('Overlap');

export type OverlapVerdict = 'keep' | 'cut';
export interface OverlapDecision { verdict: OverlapVerdict; by: 'rule' | 'model' | 'late' | 'error' | 'none'; ms: number }

const norm = (s: string) => s.toLowerCase().replace(/[’']/g, "'").replace(/[^\p{L}\p{N}' -]+/gu, ' ').replace(/\s+/g, ' ').trim();
/** Wants the floor, whatever else is said: stop/hold words, a correction opener, a question word. */
const HARD = /\b(?:stop|wait|no|nope|nah|hold on|hang on|hold up|pause|cancel|quiet|enough|shut up|never ?mind|actually|but|sorry what|what|why|how|who|when|where|which|can you|could you|tell me|i mean|excuse me)\b/;
/** Listener tokens: acks, agreement, reactions, laughter. An overlap made ONLY of these is a cue, not a turn. */
const ACK = new Set(('yeah yea yes yep yup ya ok okay k right sure true totally exactly definitely absolutely indeed ' +
  'mm mmm hmm mhm mm-hm uh-huh uh huh aha ah oh ooh wow nice cool great awesome perfect good fine ' +
  'interesting gotcha got it i see makes sense fair agreed correct of course for sure lol ha haha hahaha heh hehe ' +
  'yeah yeah alright all right sweet neat oh nice oh wow oh cool oh okay oh yeah oh right that\'s right that\'s true ' +
  'thats right thats true love it i know me too same').split(' '));
const PHRASES = ['got it', 'i see', 'makes sense', 'of course', 'for sure', 'all right', "that's right", "that's true", 'thats right', 'thats true', 'love it', 'i know', 'me too', 'uh huh'];

/** 0ms verdict for the clear cases; undefined = ambiguous (ask the model). */
export function overlapRule(text: string): OverlapVerdict | undefined {
  if (/\?/.test(text)) return 'cut';
  let t = norm(text);
  if (!t) return undefined;
  if (HARD.test(t)) return 'cut';
  for (const p of PHRASES) t = t.split(p).join(' ');
  const words = t.split(/[\s-]+/).filter(Boolean);
  if (words.every((w) => ACK.has(w) || /^(?:ha)+h?$|^(?:he)+h?$|^m+(?:hm+)?$|^h?m+$/.test(w))) return 'keep';
  return undefined;
}

/** Fast yes/no: is this overlap only a listener cue? (groq qwen, reasoning off: ~100-150ms — see fastChannelDecider.) */
export function fastOverlapModel(ai: ChatLike, model = 'groq/qwen/qwen3.8-27b'): (heard: string, signal: AbortSignal) => Promise<OverlapVerdict | null> {
  return async (heard, signal) => {
    const prompt = 'Live voice call. The assistant is mid-sentence and the user just said this over it: "' + heard + '"\n' +
      'Is it ONLY a listener cue — agreement, acknowledgement, a short reaction or laugh — so the assistant should keep talking? ' +
      'Answer no if they want the floor: a question, objection, correction, request, or new information.\nAnswer yes or no.';
    const reasoning = /qwen/.test(model) ? { providerOptions: { reasoning_effort: 'none' } } : /gpt-oss/.test(model) ? { providerOptions: { reasoning_effort: 'low' } } : {};
    const r = (await ai.chat({ model, messages: [{ role: 'user', content: prompt }], stream: false, signal, temperature: 0, maxTokens: /gpt-oss/.test(model) ? 200 : 3, ...reasoning })) as ChatResponse;
    const a = (r.content ?? '').trim().toLowerCase();
    return /^yes/.test(a) ? 'keep' : /^no/.test(a) ? 'cut' : null;
  };
}

export class OverlapJudgeOptions {
  /** The fast model for ambiguous overlaps; absent → ambiguous cuts. */
  model?: (heard: string, signal: AbortSignal) => Promise<OverlapVerdict | null>;
  /** A model verdict later than this is dropped → cut. */
  budgetMs = 300;
  onDecision: (heard: string, d: OverlapDecision) => void = () => {};
  now: () => number = () => performance.now();
}

/** Rules first, then the fast model within budget; anything undecided cuts (logged). Never rejects. */
export class OverlapJudge {
  public constructor(public options?: Partial<OverlapJudgeOptions>) {
    this.options = { ...new OverlapJudgeOptions(), ...options };
  }
  private get o(): OverlapJudgeOptions { return this.options as OverlapJudgeOptions; }

  async judge(heard: string): Promise<OverlapDecision> {
    const t0 = this.o.now();
    const done = (verdict: OverlapVerdict, by: OverlapDecision['by']) => {
      const d = { verdict, by, ms: Math.round(this.o.now() - t0) };
      if (by === 'late' || by === 'error' || by === 'none') log.warn(`overlap "${heard.slice(0, 60)}": no verdict (${by}, ${d.ms}ms) → cut`);
      else log.debug(`overlap "${heard.slice(0, 60)}": ${verdict} by ${by} ${d.ms}ms`);
      this.o.onDecision(heard, d);
      return d;
    };
    const rule = overlapRule(heard);
    if (rule) return done(rule, 'rule');
    if (!this.o.model) return done('cut', 'none');
    const ctl = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<'late'>((r) => { timer = setTimeout(() => { ctl.abort(); r('late'); }, this.o.budgetMs); });
    try {
      const v = await Promise.race([this.o.model(heard, ctl.signal).catch((e) => { log.warn(`overlap model failed: ${(e as Error).message}`); return 'error' as const; }), late]);
      if (v === 'late' || v === 'error') return done('cut', v);
      return v ? done(v, 'model') : done('cut', 'error');
    } finally { clearTimeout(timer); }
  }
}

/** The fast overlap model hosts wire (AGENTX_CHANNEL_DECIDER_MODEL, else groq qwen with GROQ_API_KEY — as channelDecide). */
export function overlapModelFor(ai: ChatLike, env: Record<string, string | undefined> = process.env): OverlapJudgeOptions['model'] {
  const model = env.AGENTX_CHANNEL_DECIDER_MODEL || undefined;
  return model || env.GROQ_API_KEY ? fastOverlapModel(ai, model) : undefined;
}

/** Soft barge-in per `--channel-mode`: turn keeps today's hard barge; normal/chatty judge overlaps. */
export const softBarge = (mode?: string): boolean => mode !== 'turn';
